import React from 'react';
import { Badge } from '../components/ui/badge';
import { FileText, Clock, DollarSign, BarChart3, Zap } from 'lucide-react';

const DIFFICULTY_MAP = {
  easy: { label: 'Easy', className: 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]' },
  medium: { label: 'Medium', className: 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)]' },
  hard: { label: 'Hard', className: 'bg-[var(--brand-danger)]/10 text-[var(--brand-danger)]' },
};

const STATUS_MAP = {
  pending: { label: 'Available', className: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20' },
  assigned: { label: 'Assigned', className: 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)] border-[var(--brand-warning)]/20' },
  typing: { label: 'Typing', className: 'bg-[var(--brand-warning)]/10 text-[var(--brand-warning)] border-[var(--brand-warning)]/20' },
  review: { label: 'In Review', className: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20' },
  done: { label: 'Done', className: 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] border-[var(--brand-accent)]/20' },
};

export default function TranscriberTaskCard({ doc, onAccept, onOpen }) {
  const difficulty = DIFFICULTY_MAP[doc.difficulty] || DIFFICULTY_MAP.medium;
  const status = STATUS_MAP[doc.status] || STATUS_MAP.pending;
  const price = doc.selected_price || doc.offered_price || 0;
  const estMin = doc.estimated_minutes || 10;
  const hourlyRate = doc.hourly_rate || (price > 0 ? Math.round((price / estMin) * 60) : 0);
  const pages = doc.page_count || 1;

  return (
    <div
      data-testid={`transcriber-card-${doc.id}`}
      className="bg-white border border-[var(--brand-border)] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={() => onOpen?.(doc)}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FileText size={16} className="text-[var(--brand-text-sec)] flex-shrink-0" />
            <h3 className="text-sm font-semibold tracking-tight text-[var(--brand-text)] truncate">
              {doc.title}
            </h3>
          </div>
          <Badge className={`rounded-sm text-[10px] ml-2 flex-shrink-0 ${status.className}`}>
            {status.label}
          </Badge>
        </div>

        {/* Description */}
        {doc.description && (
          <p className="text-xs text-[var(--brand-text-sec)] mb-3 line-clamp-1">{doc.description}</p>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Pages</p>
            <p className="text-sm font-bold font-mono text-[var(--brand-text)]">{pages}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Difficulty</p>
            <Badge className={`rounded-sm text-[10px] ${difficulty.className}`}>
              {difficulty.label}
            </Badge>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Est Time</p>
            <p className="text-sm font-bold font-mono text-[var(--brand-text)]">{estMin}m</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-[var(--brand-text-sec)] uppercase tracking-wider">Pay</p>
            <p className="text-sm font-bold font-mono text-[var(--brand-primary)]">${price.toFixed(2)}</p>
          </div>
        </div>

        {/* Hourly rate bar */}
        <div className="flex items-center justify-between bg-[var(--brand-bg)] border border-[var(--brand-border)] px-3 py-2">
          <div className="flex items-center gap-1.5">
            <BarChart3 size={14} className="text-[var(--brand-text-sec)]" />
            <span className="text-xs text-[var(--brand-text-sec)]">Hourly Rate</span>
          </div>
          <span className="text-sm font-black font-mono text-[var(--brand-accent)]" data-testid={`hourly-rate-${doc.id}`}>
            ~${hourlyRate}/hr
          </span>
        </div>
      </div>

      {/* Accept button (for available tasks) */}
      {doc.status === 'pending' && onAccept && (
        <button
          data-testid={`accept-task-${doc.id}`}
          onClick={(e) => { e.stopPropagation(); onAccept(doc); }}
          className="w-full bg-[var(--brand-primary)] text-white py-3 text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2"
        >
          <Zap size={14} /> Accept Task
        </button>
      )}
    </div>
  );
}
